import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tv } from '@/utils/tv';
import { AutoHideScrollArea } from '@/components/AutoHideScrollArea';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Close } from '@/components/Icons/Close';
import { Star } from '@/components/Icons/Star';
import { StarFilled } from '@/components/Icons/StarFilled';
import { Radio } from '@/components/Radio';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { useSurveyFill } from '@/pages/Mail/hooks/useSurveyFill';
import { SurveySubmitConfirm } from '@/pages/Mail/components/SurveySubmitConfirm';
import { SurveySubmitConfirmSheet } from '@/pages/Mail/components/SurveySubmitConfirmSheet';
import {
  BorderRadius,
  ButtonVariant,
  Size,
  SurveyQuestionType,
} from '@/types/components';

const RATING_STARS = 5;
const STAR_STROKE_WIDTH = '1';

const { SingleAnswer, MultiAnswer, Feedback, FreeForm } = SurveyQuestionType;

const optionRow = tv({
  base: 'flex min-h-[54px] cursor-pointer items-center justify-between gap-3 rounded-xl border py-2 pr-3 pl-4',
  variants: {
    isSelected: {
      true: 'border-primary bg-info-surface',
      false: 'border-divider bg-on-primary-surface',
    },
  },
});

const answerField = tv({
  base: 'text-body-14 resize-none rounded-xl border bg-on-primary-surface p-4 text-foreground outline-none placeholder:text-subtle-foreground',
  variants: {
    isOverCharLimit: {
      true: 'border-destructive',
      false: 'border-divider',
    },
    isFreeForm: {
      true: 'min-h-[280px] md:min-h-[240px]',
      false: 'min-h-[152px] md:min-h-[94px]',
    },
  },
});

const optionLabel = tv({
  base: 'text-subhead-14',
  variants: {
    isSelected: {
      true: 'text-primary',
      false: 'text-muted-foreground',
    },
  },
});

interface Props {
  surveyId: string;
  questionIds: string[];
  isMobile: boolean;
  onExit: () => void;
  onSubmitted: () => void;
  onError: () => void;
}

export const SurveyFillView: React.FC<Props> = ({
  surveyId,
  questionIds,
  isMobile,
  onExit,
  onSubmitted,
  onError,
}) => {
  const { t } = useTranslation();
  const [hoveredRating, setHoveredRating] = useState(0);
  const {
    question,
    questionNumber,
    totalQuestions,
    answer,
    maxAnswerLength,
    remainingChars,
    selectedRating,
    isQuestionLoading,
    isAnswering,
    isOverCharLimit,
    isAnswerFilled,
    isFirstQuestion,
    isSubmitConfirmOpen,
    selectSingleOption,
    selectRating,
    toggleOption,
    setTextValue,
    toggleOtherAnswer,
    goNext,
    goBack,
    finishSurvey,
    closeSubmitConfirm,
  } = useSurveyFill({ surveyId, questionIds, onSubmitted, onError });

  if (isSubmitConfirmOpen && !isMobile) {
    return (
      <SurveySubmitConfirm
        onBack={closeSubmitConfirm}
        onSubmit={() => finishSurvey()}
      />
    );
  }

  if (isQuestionLoading || !question) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <PageSpinner className="size-[22px] text-subtle-foreground" />
      </div>
    );
  }

  const { type, description, isOtherAnswerEnabled, answerOptions } = question;
  const isSingleAnswer = type === SingleAnswer;
  const isMultiAnswer = type === MultiAnswer;
  const isFeedback = type === Feedback;
  const isFreeForm = type === FreeForm;
  const isTextAnswerVisible = isFreeForm || answer.isOtherAnswerSelected;
  const visibleRating = hoveredRating || selectedRating;

  const charLimit = (
    <span
      className={`text-body-13 shrink-0 tabular-nums ${isOverCharLimit ? 'text-destructive' : 'text-subtle-foreground'}`}
    >
      {t('mail.surveys.detail.fill.charLimit', { limit: remainingChars })}
    </span>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 justify-end px-4 pt-[calc(16px+env(safe-area-inset-top))] pb-3 md:hidden">
        <button
          type="button"
          aria-label={t('global.close')}
          onClick={onExit}
          className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground"
        >
          <Close className="size-6" />
        </button>
      </div>

      <AutoHideScrollArea className="flex min-h-0 flex-1 flex-col">
        <div className="flex flex-col gap-4 px-4 pt-4 pb-4 md:px-5 md:pt-0 md:pb-5">
          <div className="flex flex-col gap-2 md:gap-1">
            <div className="flex flex-col gap-1">
              <p className="text-caption-13 text-subtle-foreground md:text-body-14">
                {t('mail.surveys.detail.fill.progress', {
                  current: questionNumber,
                  total: totalQuestions,
                })}
              </p>
              <div className="flex items-baseline justify-between gap-3">
                <p className="text-callout-16 text-foreground">{description}</p>
                {isFreeForm && charLimit}
              </div>
            </div>
            {isMultiAnswer && (
              <p className="text-body-13 text-subtle-foreground md:text-caption-12">
                {t('mail.surveys.detail.fill.multiAnswerHint')}
              </p>
            )}
          </div>

          {(isSingleAnswer || isMultiAnswer) && (
            <div className="flex flex-col gap-2">
              {answerOptions.map((option) => {
                const isSelected = answer.selectedOptions.includes(
                  option.orderNumber,
                );

                return (
                  <div
                    key={option.orderNumber}
                    onClick={() =>
                      isSingleAnswer
                        ? selectSingleOption(option.orderNumber)
                        : toggleOption(option.orderNumber)
                    }
                    className={optionRow({ isSelected })}
                  >
                    <span className={optionLabel({ isSelected })}>
                      {option.description}
                    </span>
                    <span className="pointer-events-none flex size-6 shrink-0 items-center justify-center p-0.5">
                      {isSingleAnswer && <Radio checked={isSelected} />}
                      {isMultiAnswer && (
                        <Checkbox
                          checked={isSelected}
                          isRound={isMobile}
                          onChange={() => {}}
                        />
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {isOtherAnswerEnabled && (
            <div className="flex items-center justify-between gap-3">
              <Checkbox
                checked={answer.isOtherAnswerSelected}
                label={t('mail.surveys.detail.fill.otherAnswer')}
                onChange={toggleOtherAnswer}
              />
              {answer.isOtherAnswerSelected && charLimit}
            </div>
          )}

          {isTextAnswerVisible && (
            <div className="flex flex-col gap-1">
              <textarea
                value={answer.textValue}
                placeholder={t('mail.surveys.detail.fill.answerPlaceholder')}
                onChange={(event) => setTextValue(event.target.value)}
                className={answerField({ isOverCharLimit, isFreeForm })}
              />
              {isOverCharLimit && (
                <p className="text-body-13 text-destructive">
                  {t('mail.surveys.detail.fill.charLimitError', {
                    limit: maxAnswerLength,
                  })}
                </p>
              )}
            </div>
          )}

          {isFeedback && (
            <div
              onMouseLeave={() => setHoveredRating(0)}
              className="flex items-center justify-center gap-6 pt-4 md:gap-3 md:pt-2"
            >
              {Array.from({ length: RATING_STARS }, (_, position) => {
                const rating = position + 1;
                const isActive = rating <= visibleRating;

                return (
                  <button
                    key={rating}
                    type="button"
                    aria-label={`${rating}`}
                    onMouseEnter={() => setHoveredRating(rating)}
                    onClick={() => selectRating(rating)}
                    className="flex cursor-pointer items-center justify-center max-md:size-10"
                  >
                    {isActive && rating <= selectedRating && (
                      <StarFilled className="size-8 text-accent" />
                    )}
                    {isActive && rating > selectedRating && (
                      <Star
                        strokeWidth={STAR_STROKE_WIDTH}
                        className="size-8 text-accent"
                      />
                    )}
                    {!isActive && (
                      <Star
                        strokeWidth={STAR_STROKE_WIDTH}
                        className="size-8 text-subtle-foreground"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </AutoHideScrollArea>

      <div className="flex shrink-0 items-center gap-3 border-t border-divider px-4 pt-4 pb-[calc(16px+env(safe-area-inset-bottom))] md:justify-end md:border-t-0 md:px-5 md:pt-5 md:pb-6">
        <Button
          variant={ButtonVariant.Secondary}
          size={isMobile ? Size.Large : Size.Medium}
          radius={isMobile ? BorderRadius.Small : BorderRadius.Pill}
          className={isMobile ? 'h-14 flex-1' : ''}
          onClick={isFirstQuestion ? onExit : goBack}
        >
          {isFirstQuestion
            ? t('mail.surveys.detail.fill.exit')
            : t('mail.surveys.detail.fill.back')}
        </Button>
        <Button
          variant={ButtonVariant.Primary}
          size={isMobile ? Size.Large : Size.Medium}
          radius={isMobile ? BorderRadius.Small : BorderRadius.Pill}
          className={isMobile ? 'h-14 flex-1' : ''}
          isLoading={isAnswering}
          disabled={!isAnswerFilled || isOverCharLimit}
          onClick={goNext}
        >
          {t('mail.surveys.detail.fill.next')}
        </Button>
      </div>

      {isMobile && isSubmitConfirmOpen && (
        <SurveySubmitConfirmSheet
          onClose={closeSubmitConfirm}
          onSubmit={() => finishSurvey()}
        />
      )}
    </div>
  );
};
